import { TypedPolicy } from '@/react/portainer/policies/types';
import { useDockerRBACInitialValues } from '@/react/portainer/policies/components/docker/rbac/useDockerRBACInitialValues';
import { pluralize } from '@/react/common/string-utils';

import { FormSection } from '@@/form-components/FormSection';
import { Badge } from '@@/Badge';
import { TextTip } from '@@/Tip/TextTip';

import { PolicyTableWrapper } from './PolicyTableWrapper';

interface Props {
  policy: TypedPolicy<unknown>;
}

export function DockerRBACPolicyReadOnly({ policy }: Props) {
  const values = useDockerRBACInitialValues(policy);
  const accessList = values.accessList || [];

  if (accessList.length === 0) {
    return (
      <FormSection title="Access List">
        <TextTip>No access configured for this policy.</TextTip>
      </FormSection>
    );
  }

  const userCount = accessList.filter((a) => a.Type === 'user').length;
  const teamCount = accessList.filter((a) => a.Type === 'team').length;

  return (
    <FormSection title="Access List">
      <PolicyTableWrapper
        caption="Docker RBAC access list for this policy"
        data-cy="docker-rbac-policy-readonly-table"
      >
        <thead className="bg-gray-3 th-dark:bg-gray-9">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Role</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-5 th-dark:divide-gray-7">
          {accessList.map((access) => (
            <tr key={`${access.Type}-${access.Id}`}>
              <td className="px-4 py-3 text-sm font-medium">{access.Name}</td>
              <td className="px-4 py-3">
                <Badge type={access.Type === 'user' ? 'info' : 'warn'}>
                  {access.Type === 'user' ? 'User' : 'Team'}
                </Badge>
              </td>
              <td className="px-4 py-3 text-sm">{access.Role.Name}</td>
            </tr>
          ))}
        </tbody>
      </PolicyTableWrapper>

      <div className="mt-3 text-sm text-muted">
        Total: <span className="font-medium">{userCount}</span>{' '}
        {pluralize(userCount, 'user')},{' '}
        <span className="font-medium">{teamCount}</span>{' '}
        {pluralize(teamCount, 'team')}
      </div>
    </FormSection>
  );
}
